import React from 'react';
import fr from 'facet-rest';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { getSectionCardStyle } from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/styles';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import {
  useManagedAccountPageContext,
} from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/Context/ManagedAccountPageContext';
import Subtitle from 'Components/ComponentLibrary/Atoms/Text/Subtitle';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import useMobileLayout from 'Hooks/useMobileLayout';
import AccountPerformanceCommentaryCard
  from 'Components/ComponentLibrary/Organisms/AccountPerformanceCommentaryCard/AccountPerformanceCommentaryCard';
import FAQButton from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/FAQButton';
import useManagedAccountPageEventLogger
  from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/managedAccountPage.eventLogging';
import { FDHeadlineText, FDStack } from '@facet-wealth/design';
import PortfolioSection from './PortfolioSection';
import useManagedAccountPerformanceDashboard from './useAccountPerformanceDashboard';
import AccountPerformance from './AccountPerformance';

const useStyles = buildUseStyles('AccountPerformanceDashboard', (theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(5),
  },
  sectionDescription: {
    marginBottom: theme.spacing(2),
  },
  card: {
    ...getSectionCardStyle({ theme }),
  },
  noPerformanceCard: {
    backgroundColor: theme.palette.facet.lightGray,
    boxShadow: 'none',
  },
  chartCard: {
    minWidth: theme.dimensions.xs,
    flex: 7,
    flexBasis: '700px',
  },
  highlightedCommentaryCard: {
    minWidth: theme.dimensions.xs,
    flex: 3,
    flexBasis: '300px',
  },
  cardGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  historicalCommentaries: {
    display: 'grid',
    gridTemplateColumns: ({ isMobile }) => (isMobile ? '1fr' : 'repeat(4, 1fr)'),
    gap: theme.spacing(2),
    overflowX: ({ isMobile }) => (isMobile ? 'hidden' : 'auto'),
    paddingBottom: theme.spacing(2),
  },
  historicalCommentaryCard: {
    minWidth: ({ isMobile }) => (isMobile ? 0 : 275),
  },
  disclaimerText: {
    padding: theme.spacing(1),
  },
}));

export default function AccountPerformanceDashboard() {
  const isMobile = useMobileLayout();
  const classes = useStyles({ isMobile });

  const {
    currentTabResource,
    managedAccountPageResource,
    toggleShowBenchmark,
    showBenchmark,
  } = useManagedAccountPageContext();

  const {
    isLoading,
    isError,
    performanceDashboard,
    portfolio,
    benchmarkedPerformance,
    highlightedCommentary,
    historicalCommentaries,
    setBenchmarkedPerformance,
  } = useManagedAccountPerformanceDashboard(currentTabResource);

  const { logCommentaryFeedbackEvent } = useManagedAccountPageEventLogger();

  const handleOnFeedbackSubmitted = (value) => {
    logCommentaryFeedbackEvent('feedback submitted', {
      feedback: value,
      accountId: performanceDashboard.id(),
      commentaryLabel: highlightedCommentary.attr('label'),
    });
  };

  if (isError) {
    return <Box><Subtitle value="Could not load the information. Please retry later." /></Box>;
  }

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (!performanceDashboard) {
    return <Box><Subtitle value="Could not load the information. Please retry later." /></Box>;
  }

  const hasPortfolio = !!portfolio;
  const isPerformanceDataMissing = !!performanceDashboard?.attr('noPerformanceDataMessage');
  const hasLegacyAssets = performanceDashboard?.attr('hasLegacyHoldings') === 'true';
  const legacyAssetDescription = performanceDashboard?.meta().field('hasLegacyHoldings')?.attr('description');
  const hasBenchmarkedPerformance = !isPerformanceDataMissing && !!benchmarkedPerformance;
  const hasHighlightedCommentary = !!highlightedCommentary;
  const hasHistoricalCommentaries = !!historicalCommentaries?.resources().list().length;

  const modelChangeEventsPageLink = performanceDashboard?.relationship(fr.relationships.modelChangeEventsPage.name)?.link('related');

  async function setBenchmarkedPerformanceFromCommentary(commentary) {
    if (!commentary?.relationship(fr.relationships.benchmarkedPerformance.name)?.link('related')) return;

    const [data] = await commentary.relationship(fr.relationships.benchmarkedPerformance.name).link('related').get();
    setBenchmarkedPerformance(data);
  }

  return <>
    <Box className={classes.root}>
      {/* portfolio */}
      {hasPortfolio ? <>
        <Box>
          <FDHeadlineText value={'Your Investment Mix'} />
          <br />
          <Box className={classes.card}>
            <PortfolioSection
              allocationModelDescription={managedAccountPageResource.attr('allocationModelDescription')}
              performanceAllocationTitle={managedAccountPageResource.attr('performanceAllocationTitle')}
              riskToleranceDetail={managedAccountPageResource.attr('riskToleranceDetail')}
              managedAccount={managedAccountPageResource}
              portfolio={portfolio}
              modelChangeEventsPageLink={modelChangeEventsPageLink}
              hasLegacyAssets={hasLegacyAssets}
              legacyAssetsDescription={legacyAssetDescription}
            />
          </Box>
        </Box>
      </> : null}

      {isPerformanceDataMissing && (
        <Box>
          <FDHeadlineText value={'Performance'} />
          <br />
          <Box className={classes.card}>
            <Box className={classNames(classes.card, classes.noPerformanceCard)}>
              <Typography variant={'h5'} weight={'medium'}>No Performance Data Available</Typography>
              <Typography color={'primaryGray2'} variant={'h5'}>{performanceDashboard.attr('noPerformanceDataMessage')}</Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* benchmarked performance and highlighted commentary */}
      {(hasBenchmarkedPerformance || hasHighlightedCommentary) ? <>
        <Box>
          <FDStack direction={'row'}>
            <FDHeadlineText value={'Performance'} />
            {!!managedAccountPageResource.relationship(fr.relationships.faq.name) && <>
              <FAQButton link={managedAccountPageResource.relationship(fr.relationships.faq.name).link('related')} />
            </>}
          </FDStack>
          <br />
          {!isMobile ? <Typography className={classes.sectionDescription} color="primaryGray" variant="h5">To compare your account&#39;s performance in the market we used an industry benchmark which has a similar allocation of stock and bond options. Investment performance is not indicative of the progress towards your goals, if you have questions on your progress, please talk to your planner.</Typography> : null}
          <Box className={classes.cardGroup}>

            <Box className={[classes.card, classes.chartCard]}>
              <AccountPerformance
                benchmarkedPerformance={benchmarkedPerformance}
                setBenchmarkedPerformance={setBenchmarkedPerformance}
                showBenchmark={showBenchmark}
                toggleShowBenchmark={toggleShowBenchmark}
              />
            </Box>

            {hasHighlightedCommentary && (
            <AccountPerformanceCommentaryCard
              className={classes.highlightedCommentaryCard}
              label={highlightedCommentary.attr('label')}
              startDate={highlightedCommentary.attr('startDate')}
              endDate={highlightedCommentary.attr('endDate')}
              performance={highlightedCommentary.attr('performance')}
              benchmark={highlightedCommentary.attr('benchmark')}
              commentary={highlightedCommentary.attr('commentary')}
              lastUpdated={highlightedCommentary.attr('lastUpdated')}
              chartData={highlightedCommentary.attr('chartData')}
              isHighlight
              showBenchmark={showBenchmark}
              onClickDateRange={() => setBenchmarkedPerformanceFromCommentary(highlightedCommentary)}
              feedbackLink={highlightedCommentary?.relationship(fr.relationships.userFeedback.name)?.link('related')}
              handleOnFeedbackSubmitted={handleOnFeedbackSubmitted}
            />
            )}
          </Box>
        </Box>
      </> : null}

      {/*  historical commentaries */}
      {(hasHistoricalCommentaries) ? <>
        <Box>
          <FDHeadlineText value={'Historical Returns'} />
          <br />
          <Box className={classes.historicalCommentaries}>
            {historicalCommentaries.resources().list().map((commentary) => (
              <AccountPerformanceCommentaryCard
                key={commentary.id()}
                className={classes.historicalCommentaryCard}
                label={commentary.attr('label')}
                startDate={commentary.attr('startDate')}
                endDate={commentary.attr('endDate')}
                performance={commentary.attr('performance')}
                benchmark={commentary.attr('benchmark')}
                commentary={commentary.attr('commentary')}
                lastUpdated={commentary.attr('lastUpdated')}
                chartData={commentary.attr('chartData')}
                showBenchmark={showBenchmark}
                onClickDateRange={() => setBenchmarkedPerformanceFromCommentary(commentary)}
              />
            ))}
          </Box>
        </Box>
      </> : null}
      <Box className={classes.disclaimerText}>
        <Typography color={'primaryGray2'} variant={'body1'}>{'Past performance is not a guarantee of future performance. The current performance of any Facet model or securities may be lower or higher than the past performance. No guarantee or representation is made that an investment in Facet will be successful. An Investment in Facet’s securities involves various risks. Only investors who can bear this type of economic risk and the potential loss of their entire investment should invest in Facet’s securities.'}</Typography>
      </Box>
    </Box>
  </>;
}
